import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCheckCircle, FiClock, FiAlertTriangle, FiUser, FiFileText, 
  FiArrowRight, FiFilter, FiActivity, FiLayers, FiMessageSquare,
  FiDollarSign, FiTool, FiClipboard
} = FiIcons;

const WORKFLOW_STEPS = [
  { 
    id: 1, 
    title: "Maintenance Intake", 
    desc: "Tenant submits HVAC issue via Portal.", 
    status: "success",
    module: "Unit Overview",
    icon: FiMessageSquare,
    details: "Unit 4B reported cooling failure at 2:14 PM. Automated priority: High."
  },
  { 
    id: 2, 
    title: "Vendor Dispatch", 
    desc: "Assigning 'CoolAir Systems' to ticket.", 
    status: "in-progress",
    module: "Vendor Tasks",
    icon: FiTool,
    details: "Checking vendor availability and insurance compliance..."
  },
  { 
    id: 3, 
    title: "Quote Approval", 
    desc: "Owner notification for $450 repair cost.", 
    status: "warning",
    module: "Owner Report",
    icon: FiDollarSign,
    details: "Awaiting approval from Property Owner (Sarah Mitchell)."
  },
  { 
    id: 4, 
    title: "Work Execution", 
    desc: "Technician on-site with digital keys.", 
    status: "before",
    module: "Maintenance Tickets",
    icon: FiClipboard,
    details: "Scheduled for tomorrow, 10:00 AM. Entry code generated."
  },
  { 
    id: 5, 
    title: "Tenant Sign-off", 
    desc: "Digital verification of resolved issue.", 
    status: "before",
    module: "Tenant Notes",
    icon: FiUser,
    details: "Automated SMS will be sent once vendor marks 'Complete'."
  },
  { 
    id: 6, 
    title: "Payment Processing", 
    desc: "Auto-debit from maintenance reserve.", 
    status: "before",
    module: "Payment Status",
    icon: FiDollarSign,
    details: "Invoice matching and automated ledger entry."
  },
  { 
    id: 7, 
    title: "Audit Archiving", 
    desc: "Snapshot added to lease timeline.", 
    status: "before",
    module: "Lease Timeline",
    icon: FiLayers,
    details: "Historical record updated for end-of-year tax reporting."
  }
];

const SAMPLE_DATA = [
  { id: 1, unit: "101", tenant: "Alice Vance", rent: "$1,200", status: "Paid", type: "Studio" },
  { id: 2, unit: "204", tenant: "Bob Smith", rent: "$2,450", status: "Overdue", type: "2 Bed" },
  { id: 3, unit: "305", tenant: "Charlie Day", rent: "$1,800", status: "Pending", type: "1 Bed" },
  { id: 4, unit: "412", tenant: "Diana Prince", rent: "$3,100", status: "Paid", type: "Penthouse" },
];

export default function Demo() {
  const [activeStep, setActiveStep] = useState(1);
  const [filterStatus, setFilterStatus] = useState('All');
  const [simulatedState, setSimulatedState] = useState('success');

  const filteredData = filterStatus === 'All' 
    ? SAMPLE_DATA 
    : SAMPLE_DATA.filter(d => d.status === filterStatus);

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="p-4 md:p-8 space-y-12 max-w-7xl mx-auto"
    >
      {/* Header */}
      <div className="border-b border-[#004B87]/20 pb-6">
        <h2 className="text-3xl font-light uppercase tracking-[0.2em] text-[#004B87]">Demo / How It Works</h2>
        <p className="text-sm font-mono text-[#004B87]/60 mt-2 max-w-2xl">
          Experience the PropertyPulse ecosystem. This workspace simulates a real-time team environment 
          designed for small property managers to automate complex multi-stakeholder workflows.
        </p>
      </div>

      {/* Section 1: Try this flow */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-px bg-[#C1440E]"></div>
          <h3 className="text-sm font-bold uppercase tracking-widest">Try this flow</h3>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-2">
            {WORKFLOW_STEPS.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`w-full text-left p-4 tech-border transition-all flex items-center gap-4
                  ${activeStep === step.id ? 'bg-[#C1440E]/5 border-[#C1440E]' : 'bg-[#FFFFF0]/50 border-[#004B87]/20 hover:border-[#004B87]/50'}
                `}
              >
                <div className={`w-8 h-8 flex items-center justify-center text-lg
                  ${activeStep === step.id ? 'text-[#C1440E]' : 'text-[#004B87]/40'}
                `}>
                  <SafeIcon icon={step.icon} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-mono uppercase text-[#004B87]/50">Step 0{step.id}</p>
                  <p className={`text-xs font-bold uppercase tracking-wider ${activeStep === step.id ? 'text-[#C1440E]' : ''}`}>
                    {step.title}
                  </p>
                </div>
                {activeStep === step.id && <SafeIcon icon={FiArrowRight} className="text-[#C1440E]" />}
              </button>
            ))}
          </div>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tech-panel p-8 h-full flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <span className="bg-[#004B87]/10 text-[#004B87] px-3 py-1 text-[10px] font-mono uppercase tracking-widest border border-[#004B87]/20">
                      Module: {WORKFLOW_STEPS[activeStep - 1].module}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full animate-pulse 
                        ${WORKFLOW_STEPS[activeStep - 1].status === 'success' ? 'bg-green-500' : 
                          WORKFLOW_STEPS[activeStep - 1].status === 'warning' ? 'bg-[#C1440E]' : 
                          WORKFLOW_STEPS[activeStep - 1].status === 'in-progress' ? 'bg-[#004B87]' : 'bg-gray-300'}
                      `}></div>
                      <span className="text-[10px] font-mono uppercase text-[#004B87]/60">
                        {WORKFLOW_STEPS[activeStep - 1].status.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                  <h4 className="text-2xl font-light uppercase tracking-widest mb-4">
                    {WORKFLOW_STEPS[activeStep - 1].title}
                  </h4>
                  <p className="text-[#004B87]/80 text-lg mb-6">
                    {WORKFLOW_STEPS[activeStep - 1].desc}
                  </p>
                  <div className="bg-[#004B87]/5 p-4 border border-dashed border-[#004B87]/30 font-mono text-xs leading-relaxed">
                    <span className="text-[#C1440E] mr-2">&gt;</span>
                    {WORKFLOW_STEPS[activeStep - 1].details}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[#004B87]/10 flex justify-between items-center">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full bg-[#004B87] border-2 border-[#FFFFF0] flex items-center justify-center text-[10px] text-[#FFFFF0] font-bold">
                        {String.fromCharCode(64 + i)}
                      </div>
                    ))}
                    <div className="w-8 h-8 rounded-full bg-[#FFFFF0] border-2 border-[#004B87]/20 flex items-center justify-center text-[10px] text-[#004B87]/50">
                      +2
                    </div>
                  </div>
                  <button 
                    onClick={() => setActiveStep(prev => prev < 7 ? prev + 1 : 1)}
                    className="btn-rust text-xs flex items-center gap-2"
                  >
                    Next Action <SafeIcon icon={FiArrowRight} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Section 2: Sample Data */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-px bg-[#C1440E]"></div>
          <h3 className="text-sm font-bold uppercase tracking-widest">Sample Data</h3>
        </div>

        <div className="tech-panel p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <h4 className="text-sm font-bold uppercase tracking-wider">Unit Ledger Preview</h4>
            <div className="flex gap-2">
              {['All', 'Paid', 'Overdue', 'Pending'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 text-[10px] font-mono uppercase border transition-all
                    ${filterStatus === status ? 'bg-[#004B87] text-[#FFFFF0] border-[#004B87]' : 'border-[#004B87]/20 text-[#004B87]/60 hover:border-[#004B87]/50'}
                  `}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#004B87]/20 text-left text-xs font-mono uppercase text-[#004B87]/50">
                  <th className="pb-3 font-normal">Unit</th>
                  <th className="pb-3 font-normal">Tenant</th>
                  <th className="pb-3 font-normal">Type</th>
                  <th className="pb-3 font-normal">Rent</th>
                  <th className="pb-3 font-normal text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#004B87]/10">
                {filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-[#004B87]/5 transition-colors">
                    <td className="py-3 font-mono font-bold">{item.unit}</td>
                    <td className="py-3">{item.tenant}</td>
                    <td className="py-3 text-[10px] uppercase font-mono">{item.type}</td>
                    <td className="py-3">{item.rent}</td>
                    <td className="py-3 text-right">
                      <span className={`text-[10px] font-mono px-2 py-0.5 border
                        ${item.status === 'Paid' ? 'border-green-500/30 text-green-600 bg-green-50' : 
                          item.status === 'Overdue' ? 'border-[#C1440E]/30 text-[#C1440E] bg-[#C1440E]/5' : 
                          'border-yellow-500/30 text-yellow-600 bg-yellow-50'}
                      `}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 3: Simulated States */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-px bg-[#C1440E]"></div>
          <h3 className="text-sm font-bold uppercase tracking-widest">Simulated States</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Success State */}
          <div className="tech-panel p-5 border-l-4 border-l-green-500">
            <div className="flex justify-between mb-4">
              <span className="text-[10px] font-mono uppercase text-green-600 font-bold">Success</span>
              <SafeIcon icon={FiCheckCircle} className="text-green-500" />
            </div>
            <p className="text-xs font-bold uppercase mb-1">System Synced</p>
            <p className="text-[10px] text-[#004B87]/60 leading-relaxed">All property data updated. 0 conflicts detected.</p>
          </div>

          {/* In Progress State */}
          <div className="tech-panel p-5 border-l-4 border-l-[#004B87]">
            <div className="flex justify-between mb-4">
              <span className="text-[10px] font-mono uppercase text-[#004B87] font-bold">In Progress</span>
              <SafeIcon icon={FiClock} className="text-[#004B87] animate-spin-slow" />
            </div>
            <p className="text-xs font-bold uppercase mb-1">Generating Report</p>
            <p className="text-[10px] text-[#004B87]/60 leading-relaxed">Compiling Q3 financial disclosure for owners...</p>
          </div>

          {/* Warning State */}
          <div className="tech-panel p-5 border-l-4 border-l-[#C1440E]">
            <div className="flex justify-between mb-4">
              <span className="text-[10px] font-mono uppercase text-[#C1440E] font-bold">Warning</span>
              <SafeIcon icon={FiAlertTriangle} className="text-[#C1440E]" />
            </div>
            <p className="text-xs font-bold uppercase mb-1">Lease Expiring</p>
            <p className="text-[10px] text-[#004B87]/60 leading-relaxed">Unit 204 lease ends in 14 days. No renewal sent.</p>
          </div>

          {/* Empty State */}
          <div className="tech-panel p-5 border-l-4 border-l-gray-300">
            <div className="flex justify-between mb-4">
              <span className="text-[10px] font-mono uppercase text-gray-400 font-bold">Empty</span>
              <SafeIcon icon={FiActivity} className="text-gray-300" />
            </div>
            <p className="text-xs font-bold uppercase mb-1">No Alerts</p>
            <p className="text-[10px] text-[#004B87]/60 leading-relaxed">Clean slate. All maintenance tickets resolved.</p>
          </div>
        </div>
      </section>

      {/* Credit */}
      <div className="text-center pt-8 border-t border-[#004B87]/10">
        <p className="text-[10px] font-mono tracking-widest text-[#004B87]/30 uppercase">
          Made by LingLink AI and DigLab AI
        </p>
      </div>
    </motion.div>
  );
}